// Office review and completion (technical design §6.4, M6; office-review prototype). Technicians
// pause; the office checks the work and completes the job. The checklist comes from the server
// (jobs.reviewChecklist), so the Complete button and the rule cannot disagree. Prices a technician
// set below their limit wait here for the office's OK too (PriceOks.tsx).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { dateTimeText } from '@flowology/kernel';
import { RpcError } from '@flowology/client';
import { PriceOksSection } from './PriceOks.tsx';
import { liveStream } from '../connection/live.ts';
import { client } from '../api.ts';
import { Header, type NavProps } from '../Nav.tsx';
import {
  flagText,
  queueChip,
  REVIEW_EVENTS,
  sentBackWhen,
  reviewRights,
  addDonePage,
  addQueuePage,
  doneInput,
  donePage,
  doneRereadLimit,
  QUEUE_PAGE,
  queuePages,
  type DonePage,
  type FlagPage,
  type QueueJob,
  type QueuePage,
} from './review-model.ts';
import { rememberedQueue, rememberQueue } from './queue-cache.ts';
import { shownText } from '../shared/pages.ts';
import { openJob } from '../job/job-page.ts';
import { QuarantineSection } from './Quarantine.tsx';
import { LoadLine, useLadder } from '../shared/Ladder.tsx';
import { useMirror } from '../shared/mirror.ts';
import './review.css';
import '../blueprint/inbox.css';
import { useDesign } from '../blueprint/design.ts';
import { plainText } from '../shared/plain-text.ts';
import { addRows } from '../blueprint/paged.ts';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

/** What a card already knew about a job, so its sheet opens with it (L-sheet). */
type Known = { number: string; customerName: string; summary: string };

export function ReviewScreen({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const [tick, setTick] = useState(0);
  const mirroring = useMirror();
  const rights = reviewRights(nav.permissions ?? [], mirroring);

  // Live: re-read when the server says a job, visit, material or flag changed.
  useEffect(() => {
    const es = liveStream();
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (REVIEW_EVENTS.test(type)) setTick((t) => t + 1);
    });
    return () => es.close();
  }, []);

  // A job opens on its one page (job/JobRoute.tsx), over this queue: its Review area is where it's
  // reviewed and completed.
  return (
    <div className="app">
      <Header
        nav={nav}
        title="Review queue"
        lede="Techs pause; the office checks each job, then completes it."
      />
      <Queue
        tenant={nav.tenant}
        tick={tick}
        timeZone={timeZone}
        rights={rights}
        onOpen={(id) => openJob(id)}
      />
    </div>
  );
}

function Queue({
  tenant,
  tick,
  timeZone,
  rights,
  onOpen,
}: {
  tenant: string;
  tick: number;
  timeZone: string;
  rights: ReturnType<typeof reviewRights>;
  onOpen: (id: string, known: Known) => void;
}) {
  // Coming back from a job, the last queue shows at once ("Updated 10:12") while it is re-read.
  const kept = rememberedQueue(tenant);
  const bp = useDesign() === 'blueprint';
  // Blueprint shows five of the jobs completed lately, then the rest read, then "Load more"
  const [allDone, setAllDone] = useState(false);
  const [queue, setQueue] = useState<QueuePage | null>(kept?.queue ?? null);
  const [back, setBack] = useState<QueuePage>(kept?.back ?? NO_JOBS);
  const [done, setDone] = useState<DonePage>(kept?.done ?? NO_DONE);
  const [fresh, setFresh] = useState(!kept);
  const [at, setAt] = useState(kept?.at ?? null);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [moreBusy, setMoreBusy] = useState(false);
  const jobs = queue?.jobs ?? null;
  const phase = useLadder(jobs === null, attempt, failed);
  // How much of each list is open, so a refresh reads it all again and "Load more" stays open; a page
  // that comes back after a refresh began is dropped (the refresh has it).
  const shown = useRef({ queue: 0, back: 0, done: 0 });
  shown.current = { queue: jobs?.length ?? 0, back: back.jobs.length, done: done.jobs.length };
  const reads = useRef(0);
  useEffect(() => {
    setFailed(false);
    reads.current++;
    readQueue({}, queuePages(shown.current.queue))
      .then((r) => {
        setQueue(r);
        setFresh(true);
        setAt(new Date().toISOString());
        setError(null);
      })
      .catch((e: unknown) => {
        setFailed(true);
        setError(message(e));
      });
    readQueue({ sentBack: true }, queuePages(shown.current.back))
      .then(setBack)
      .catch(() => undefined);
    // the latest completed first, as many as were shown (list caps, BL-110)
    client
      .call<ViewAnswer>('views.run', doneInput(0, doneRereadLimit(shown.current.done)))
      .then((r) => setDone(donePage(r)))
      .catch(() => undefined);
  }, [tick, attempt]);
  useEffect(() => {
    if (queue && at) rememberQueue(tenant, { queue, back, done, at });
  });
  const more = (sentBack: boolean) => {
    const from = sentBack ? back.next : queue?.next;
    if (!from) return;
    const read = reads.current;
    setMoreBusy(true);
    client
      .call<QueuePage>('jobs.reviewQueue', {
        ...(sentBack ? { sentBack } : {}),
        limit: QUEUE_PAGE,
        after: from,
      })
      .then((page) => {
        if (read !== reads.current) return;
        if (sentBack) setBack((p) => addQueuePage(p, page));
        else setQueue((p) => p && addQueuePage(p, page));
      })
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setMoreBusy(false));
  };
  // Completed recently's next page, from the jobs already shown
  const moreDone = () => {
    const read = reads.current;
    setMoreBusy(true);
    client
      .call<ViewAnswer>('views.run', doneInput(done.jobs.length))
      .then((r) => read === reads.current && setDone((p) => addDonePage(p, donePage(r))))
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setMoreBusy(false));
  };
  // the rest of a list past its page: Blueprint's an underlined link, Riverstone's a quiet button
  const moreLink = (p: QueuePage | null, sentBack: boolean) =>
    p?.next && (
      <button
        type="button"
        className={bp ? 'ib-link ib-more' : 'btn soft q-more'}
        disabled={moreBusy}
        onClick={() => more(sentBack)}
      >
        Load more
      </button>
    );

  const review = jobs ?? [];
  const card = (j: { number: string; customerName: string; summary: string }) => ({
    number: j.number,
    customerName: j.customerName,
    summary: j.summary,
  });
  const awaiting = (
    <Section
      title={queue === null ? 'Awaiting review' : `Awaiting review (${queue.total})`}
      busy={(jobs === null && phase !== 'error') || !fresh}
    >
      {jobs === null ? (
        // L-stones: at most three blank stones at a card's size
        phase !== 'error' && (
          <div className={`qgrid${phase === 'wait' ? ' ldwait' : ''}`}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="ph-stone qph" aria-hidden="true" />
            ))}
          </div>
        )
      ) : review.length ? (
        <div className="qgrid">
          {review.map((j) => (
            <QueueCard key={j.jobId} job={j} onOpen={(id) => onOpen(id, card(j))} />
          ))}
        </div>
      ) : (
        <p className="muted">All caught up.</p>
      )}
      {moreLink(queue, false)}
      <LoadLine
        phase={phase}
        what="the review queue"
        {...(!fresh && kept ? { note: `Updated ${clockTime(kept.at, timeZone)}` } : {})}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    </Section>
  );
  const sentBack = back.jobs.length > 0 && (
    <Section title={`Sent back, waiting on the tech (${back.total})`}>
      <div className="qgrid">
        {back.jobs.map((j) => (
          <button key={j.jobId} className="stone qcard" onClick={() => onOpen(j.jobId, card(j))}>
            <b>
              #{j.number} · {j.customerName}
            </b>
            <span className="muted small">{plainText(j.summary)}</span>
            <span className="chip warn">Sent back{j.sentBackReason ? `: ${j.sentBackReason}` : ''}</span>
            {j.sentBackAt && <span className="muted small">Sent {sentBackWhen(j.sentBackAt, timeZone)}</span>}
          </button>
        ))}
      </div>
      {moreLink(back, true)}
    </Section>
  );
  const flagged = rights.review && <FlagsSection tick={tick} timeZone={timeZone} onOpen={onOpen} />;
  const quarantine = rights.quarantine && <QuarantineSection tick={tick} timeZone={timeZone} />;
  // estimates.approve's roles are review's: owner, manager, dispatcher (price-ok-model.ts)
  const priceOks = rights.review && <PriceOksSection tick={tick} timeZone={timeZone} canOk={rights.close} />;
  const folded = bp && !allDone && done.jobs.length > 5;
  const completed = done.jobs.length > 0 && (
    <Section title="Completed recently">
      <div className="qgrid">
        {(folded ? done.jobs.slice(0, 5) : done.jobs).map((j) => (
          <button key={j.id} className="stone qcard" onClick={() => onOpen(j.id, card(j))}>
            <b>
              #{j.number} · {j.customerName}
            </b>
            <span className="muted small">{plainText(j.summary)}</span>
            <span className="chip say ok">Completed</span>
          </button>
        ))}
      </div>
      {folded ? (
        <button type="button" className="ib-link ib-more" onClick={() => setAllDone(true)}>
          Show {done.jobs.length - 5} more
        </button>
      ) : (
        done.more && (
          <>
            <p className="muted small">{shownText(done.jobs.length, done.total)}</p>
            <button
              type="button"
              className={bp ? 'ib-link ib-more' : 'btn soft q-more'}
              disabled={moreBusy}
              onClick={moreDone}
            >
              Load more
            </button>
          </>
        )
      )}
    </Section>
  );
  // Blueprint: what waits for the office on the left, what's settled on the right
  if (bp)
    return (
      <main className="page review bp-ib">
        {error && jobs !== null && <p className="error">{error}</p>}
        <div className="ib-cols">
          <div>
            {awaiting}
            {sentBack}
            {flagged}
          </div>
          <div>
            {priceOks}
            {quarantine}
            {completed}
          </div>
        </div>
      </main>
    );
  return (
    <main className="page review">
      {error && jobs !== null && <p className="error">{error}</p>}
      {awaiting}
      {sentBack}
      {flagged}
      {quarantine}
      {priceOks}
      {completed}
    </main>
  );
}

const NO_JOBS: QueuePage = { jobs: [], total: 0, next: null };
const NO_DONE: DonePage = { jobs: [], total: 0, more: false };
/** views.run's answer, as Completed recently reads it. */
type ViewAnswer = { rows: Record<string, unknown>[]; truncated: boolean; total?: number };

/** The queue's first `pages` pages, one after another: each starts where the one before ended. */
async function readQueue(input: { sentBack?: true }, pages: number): Promise<QueuePage> {
  const page = (after?: QueuePage['next']) =>
    client.call<QueuePage>('jobs.reviewQueue', { ...input, limit: QUEUE_PAGE, ...(after ? { after } : {}) });
  let all = await page();
  for (let i = 1; i < pages && all.next; i++) all = addQueuePage(all, await page(all.next));
  return all;
}

const clockTime = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { hour: 'numeric', minute: '2-digit', timeZone })
    .format(new Date(iso))
    .replace(/\s?[ap]\.?m\.?$/i, '');

/** Open review flags across jobs (gap #18: reviewFlags.list); one opens its job, where it's resolved. */
function FlagsSection({
  tick,
  timeZone,
  onOpen,
}: {
  tick: number;
  timeZone: string;
  onOpen: (id: string, known: Known) => void;
}) {
  const bp = useDesign() === 'blueprint';
  const [flags, setFlags] = useState<FlagPage | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const reads = useRef(0);
  // only those the server places on a job (onJob): a flag the office can't open is no use here
  const page = (after?: string) =>
    client.call<FlagPage>('reviewFlags.list', { status: 'open', onJob: true, ...(after ? { after } : {}) });
  useEffect(() => {
    const read = ++reads.current;
    void page()
      .then((r) => read === reads.current && setFlags(r))
      .catch(() => read === reads.current && setFlags(null));
  }, [tick]);
  const more = () => {
    if (!flags?.next) return;
    const read = reads.current;
    setMoreBusy(true);
    void page(flags.next)
      .then(
        (r) =>
          read === reads.current &&
          setFlags((f) => f && { flags: addRows(f.flags, r.flags), total: r.total, next: r.next }),
      )
      .catch(() => undefined) // the link stays, to try again
      .finally(() => setMoreBusy(false));
  };
  if (!flags?.flags.length) return null;
  return (
    <Section title={`Flagged for the office (${flags.total})`}>
      <div className="qgrid">
        {flags.flags.map((f) => {
          const body = (
            <>
              <b>
                {f.jobNumber ? `#${f.jobNumber}${f.customerName ? ` · ${f.customerName}` : ''}` : 'A flag'}
              </b>
              <span className="muted small">{flagText(f.reason)}</span>
              <span className="chip say warn">{dateTimeText(f.createdAt, timeZone, { year: false })}</span>
            </>
          );
          return f.jobId ? (
            <button
              key={f.id}
              className="stone qcard"
              onClick={() =>
                onOpen(f.jobId!, {
                  number: f.jobNumber ?? '',
                  customerName: f.customerName ?? '',
                  summary: '',
                })
              }
            >
              {body}
            </button>
          ) : (
            <div key={f.id} className="stone qcard">
              {body}
            </div>
          );
        })}
      </div>
      {flags.next && (
        <button
          type="button"
          className={bp ? 'ib-link ib-more' : 'btn soft q-more'}
          disabled={moreBusy}
          onClick={more}
        >
          Load more
        </button>
      )}
    </Section>
  );
}

function Section({ title, busy, children }: { title: string; busy?: boolean; children: ReactNode }) {
  return (
    <section className="sect" aria-label={title} aria-busy={busy || undefined}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function QueueCard({ job, onOpen }: { job: QueueJob; onOpen: (id: string) => void }) {
  const chip = queueChip(job);
  return (
    <button className="stone qcard" onClick={() => onOpen(job.jobId)}>
      <b>
        #{job.number} · {job.customerName}
      </b>
      <span className="muted small">{plainText(job.summary)}</span>
      <span className={`chip ${chip.tone}`}>{chip.text}</span>
    </button>
  );
}
